import React from 'react'
import { getFirstNotEmpty } from '@corratech/pylot-utils'
import { useStoreConfig } from 'config'
import { getCanonical, getFileExtension, getTrimmedUrl } from '../util'
import Head from 'next/head'
import { BlogAuthorDetails } from '@pylot-data/api/operations/get-seo-details'
import { BlogArticleSchema } from '@components/seo/src/Blog/BlogArticleSchema'
import { DefaultOGImage } from '@components/seo/src/ImagePath'

export interface BlogInterface {
    title: string
    meta_title: string
    meta_keywords: string
    meta_description: string
    og_title: string
    og_description: string
    og_image: string
    og_type: string
    canonical_url: string
    author?: BlogAuthorDetails
    publish_time: string
    short_content?: string
    featured_image: string
    media_gallery?: string
    update_time?: string
    modified_time?: string
}

export type BlogProps = {
    blog: BlogInterface
    pathname: string
    titleOverride?: string
    descriptionOverride?: string
    asPath: string
}

export const Blog = ({
    blog,
    pathname,
    titleOverride,
    descriptionOverride,
    asPath
}: BlogProps): JSX.Element => {
    const config = useStoreConfig()
    const { seo, base } = config

    const title = getFirstNotEmpty([
        titleOverride,
        blog?.meta_title,
        seo.default.title
    ])

    const description = getFirstNotEmpty([
        descriptionOverride,
        blog?.meta_description,
        seo.default.description
    ])
    const ogDefaultImage = `${base.url.baseUrl}${DefaultOGImage}`
    const ogImageContent =
        blog?.og_image === 'false' || !blog?.og_image
            ? ogDefaultImage
            : getTrimmedUrl(blog?.og_image)

    return (
        <>
            <Head>
                <title>{title}</title>
                <meta name="title" content={title} />
                <meta name="keywords" content={String(blog?.meta_keywords)} />
                <meta name="description" content={String(description)} />
                <link
                    rel="canonical"
                    href={getCanonical(pathname, base.url.baseUrl)}
                />
                {/* TWITTER */}
                <meta name="twitter:title" content={title} />
                <meta name="twitter:description" content={description} />
                <meta
                    property="twitter:domain"
                    content={seo.default.twitter?.handle}
                />
                <meta
                    property="twitter:url"
                    content={getCanonical(pathname, base.url.baseUrl)}
                />
                <meta
                    name="twitter:card"
                    content={seo.default.twitter?.cardType}
                />
                {/* OPEN GRAPH */}
                <meta
                    property="og:url"
                    content={getCanonical(pathname, base.url.baseUrl)}
                />
                <meta property="og:title" content={title} />
                <meta property="og:description" content={description} />
                <meta property="og:image" content={ogImageContent} />
                <meta property="og:type" content={blog?.og_type} />
                <meta
                    property="og:image:type"
                    content={`image/${getFileExtension(ogImageContent)}`}
                />
                <meta
                    property="og:site_name"
                    content={seo.default.twitter?.site}
                />

                <meta
                    name="p:domain_verify"
                    content="f000ff9735a3c025e5f36e6833393400"
                />
            </Head>
            <BlogArticleSchema
                asPath={asPath}
                base={base}
                blogInfo={blog}
                title={title}
            />
        </>
    )
}
